import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import SeverityBadge from "@/components/shared/SeverityBadge";
import StatusBadge from "@/components/shared/StatusBadge";
import MapView from "@/components/shared/MapView";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Activity, MapPin, Truck, Clock, Filter, RotateCw, X, Phone, Shield, Heart, AlertTriangle, Navigation, User, Car, FileText, Zap, Radio, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { extractApiError, unwrapSingle } from "@/lib/api";
import { subscribeToVehicleState, subscribeToIncidents } from "@/services/incidentService";
import LocationLabel from "@/components/shared/LocationLabel";


function toText(value) {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") {
    if (("lat" in value || "latitude" in value) && ("lon" in value || "lng" in value || "longitude" in value)) {
      const lat = value.lat ?? value.latitude;
      const lon = value.lon ?? value.lng ?? value.longitude;
      return `${lat}, ${lon}`;
    }
    if (typeof value.address === "string") return value.address;
    if (typeof value.place === "string") return value.place;
    return JSON.stringify(value);
  }
  return String(value);
}

function parseCoords(item) {
  if (!item) return null;
  if (Array.isArray(item.coords) && item.coords.length === 2) return item.coords;
  if (Array.isArray(item.location_coords) && item.location_coords.length === 2) return item.location_coords;
  const location = item.location;
  if (location && typeof location === "object") {
    const lat = Number.parseFloat(location.lat ?? location.latitude);
    const lng = Number.parseFloat(location.lon ?? location.lng ?? location.longitude);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return [lat, lng];
  }
  const lat = Number.parseFloat(item.latitude ?? item.lat ?? item.lat_deg ?? item.latitude_deg);
  const lng = Number.parseFloat(item.longitude ?? item.lng ?? item.lon ?? item.longitude_deg);
  if (Number.isFinite(lat) && Number.isFinite(lng)) return [lat, lng];
  return null;
}

function normalizeVehicle(item) {
  const status = String(item?.status || item?.state || "offline").toUpperCase();
  const coords = parseCoords(item);
  const gpsSpeed = Number.parseFloat(item?.gps_speed ?? item?.speed ?? item?.telemetry?.gps_speed ?? 0);
  const obdSpeed = Number.parseFloat(item?.obd_speed ?? item?.telemetry?.obd_speed);
  const lat = Number.parseFloat(item?.lat ?? item?.latitude ?? item?.location?.lat ?? item?.location?.latitude);
  const lon = Number.parseFloat(item?.lon ?? item?.lng ?? item?.longitude ?? item?.location?.lon ?? item?.location?.lng ?? item?.location?.longitude);
  return {
    id: item?.vehicle_id || item?.id || "",
    registration: toText(item?.registration_number || item?.plate || item?.vehicle_name || "—"),
    make: item?.make || null,
    model: item?.model || null,
    type: toText(item?.vehicle_type || item?.type || "Vehicle"),
    status,
    speed: Number.isFinite(gpsSpeed) ? gpsSpeed : 0,
    gps_speed: Number.isFinite(gpsSpeed) ? gpsSpeed : 0,
    obd_speed: Number.isFinite(obdSpeed) ? obdSpeed : null,
    heading: Number.parseFloat(item?.heading ?? 0) || 0,
    location: toText(item?.location || item?.address || "—"),
    locationRaw:
      item?.location
      || item?.address
      || (Number.isFinite(lat) && Number.isFinite(lon) ? { lat, lon } : null),
    coords,
    lastUpdated: item?.updated_at || item?.timestamp || item?.event_time || item?.last_updated || "",
    telemetry: item?.telemetry || {},
    raw: item,
  };
}

function normalizeIncident(item) {
  const status = String(item?.status || "OPEN").toUpperCase();
  const severity = String(item?.severity || "low").toLowerCase();
  const coords = parseCoords(item);
  return {
    id: item?.id || "",
    type: toText(item?.incident_type || item?.type || "Incident"),
    severity,
    status,
    location: toText(item?.location || "—"),
    locationRaw: item?.location ?? null,
    coords,
    updatedAt: item?.updated_at || item?.timestamp || "",
    raw: item,
  };
}

function formatDateTime(value) {
  if (!value) return "—";
  const numeric = Number(value);
  if (Number.isFinite(numeric) && String(value).trim() !== "") {
    const millis = numeric > 1e12 ? numeric : numeric * 1000;
    const unixDate = new Date(millis);
    if (!Number.isNaN(unixDate.getTime())) return unixDate.toLocaleString();
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
}

const SEVERITY_OPTIONS = ["critical", "high", "medium", "low"];

export default function PoliceLiveMonitoring() {
  const [vehicles, setVehicles] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [vehicleDialogOpen, setVehicleDialogOpen] = useState(false);
  const [vehicleTelemetry, setVehicleTelemetry] = useState(null);
  const [telemetryLoading, setTelemetryLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mapLayer, setMapLayer] = useState("vehicles");
  const [filters, setFilters] = useState({ severity: "All", state: "", city: "", district: "" });
  const selectedVehicleIdRef = useRef(null);

  useEffect(() => {
    selectedVehicleIdRef.current = selectedVehicleId;
  }, [selectedVehicleId]);

  // Fetch initial vehicle and incident data
  const fetchVehicleState = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const params = { only_live: true, page_size: 100 };
      if (filters.state) params.state = filters.state;
      if (filters.city) params.city = filters.city;
      if (filters.district) params.district = filters.district;

      const { data } = await api.get("/vehicles/bulk-state", { params });
      const normalizedVehicles = (Array.isArray(data) ? data : data?.data || [])
        .map(normalizeVehicle)
        .filter((v) => v.id);

      setVehicles(normalizedVehicles);
      if (!selectedVehicleId && normalizedVehicles.length > 0) {
        setSelectedVehicleId(normalizedVehicles[0].id);
      }
    } catch (err) {
      setError(extractApiError(err).message || "Failed to load vehicle state.");
    } finally {
      setLoading(false);
    }
  }, [filters.state, filters.city, filters.district, selectedVehicleId]);

  // Fetch vehicle telemetry for selected vehicle
  const fetchVehicleTelemetry = useCallback(async (vehicleId) => {
    if (!vehicleId) return;
    setTelemetryLoading(true);

    try {
      const response = await api.get(`/vehicles/${vehicleId}/telemetry/latest`);
      setVehicleTelemetry(unwrapSingle(response));
    } catch (err) {
      const { message } = extractApiError(err);
      toast.error(message || "Failed to load vehicle telemetry.");
    } finally {
      setTelemetryLoading(false);
    }
  }, []);

  // Subscribe to the shared WS singleton — never open a second connection;
  // the server allows only one WS per token per session.
  useEffect(() => {
    const unsubVehicle = subscribeToVehicleState((data) => {
      const normalized = normalizeVehicle(data);
      setVehicles((prev) =>
        prev.map((v) => (v.id === normalized.id ? normalized : v))
      );
      if (selectedVehicleIdRef.current === normalized.id) {
        setVehicleTelemetry((prev) => ({ ...prev, ...data }));
      }
    });

    const unsubIncident = subscribeToIncidents((data) => {
      const normalized = normalizeIncident(data);
      if (!normalized.id) return;
      setIncidents((prev) => {
        const existing = prev.find((inc) => inc.id === normalized.id);
        return existing
          ? prev.map((inc) => (inc.id === normalized.id ? normalized : inc))
          : [...prev, normalized];
      });
    });

    return () => {
      unsubVehicle();
      unsubIncident();
    };
  }, []);

  useEffect(() => {
    fetchVehicleState();
  }, [fetchVehicleState]);

  useEffect(() => {
    if (selectedVehicleId) {
      fetchVehicleTelemetry(selectedVehicleId);
    }
  }, [selectedVehicleId, fetchVehicleTelemetry]);

  const selectedVehicle = vehicles.find((v) => v.id === selectedVehicleId);
  const mapMarkers = useMemo(() => {
    const vehicleMarkers = vehicles
      .filter((v) => Array.isArray(v.coords) && v.coords.length === 2 && typeof v.coords[0] === "number" && typeof v.coords[1] === "number")
      .map((vehicle) => ({
        coords: vehicle.coords,
        title: vehicle.registration || vehicle.type,
        subtitle: vehicle.location,
        severity: vehicle.status === "OFFLINE" ? "low" : vehicle.speed > 80 ? "high" : "medium",
        type: "vehicle",
      }));

    const incidentMarkers = incidents
      .filter((inc) => Array.isArray(inc.coords) && inc.coords.length === 2 && typeof inc.coords[0] === "number" && typeof inc.coords[1] === "number")
      .map((incident) => ({
        coords: incident.coords,
        title: incident.type,
        subtitle: incident.location,
        severity: incident.severity,
        type: "incident",
      }));

    if (mapLayer === "incidents") return incidentMarkers;
    if (mapLayer === "vehicles") return vehicleMarkers;
    return [...vehicleMarkers, ...incidentMarkers];
  }, [vehicles, incidents, mapLayer]);

  return (
    <div className="space-y-6" data-testid="police-live-monitoring">
      <PageHeader
        title="Live Monitoring"
        subtitle="Real-time vehicle positions and incident alerts via WebSocket."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="gap-2" onClick={fetchVehicleState} disabled={loading}>
              <RotateCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
            </Button>
          </div>
        }
      />

      <SectionCard>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            size="sm"
            variant={mapLayer === "vehicles" ? "default" : "outline"}
            onClick={() => setMapLayer("vehicles")}
          >
            Vehicles
          </Button>
          <Button
            size="sm"
            variant={mapLayer === "incidents" ? "default" : "outline"}
            onClick={() => setMapLayer("incidents")}
          >
            Incidents
          </Button>
          <Button
            size="sm"
            variant={mapLayer === "both" ? "default" : "outline"}
            onClick={() => setMapLayer("both")}
          >
            Both
          </Button>

          <div className="flex items-center gap-2 ml-auto">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Severity</Label>
            <Select value={filters.severity} onValueChange={(value) => setFilters({ ...filters, severity: value })}>
              <SelectTrigger className="h-10 min-w-[140px]">
                <SelectValue placeholder="All severities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All severities</SelectItem>
                {SEVERITY_OPTIONS.map((sev) => (
                  <SelectItem key={sev} value={sev}>{sev.charAt(0).toUpperCase() + sev.slice(1)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </SectionCard>

      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_380px] items-start">
        {/* Map */}
        <div className=" space-y-4 bg-white rounded-2xl border border-slate-100 p-4">
        <SectionCard padded={false}>
          <div className="rounded-2xl overflow-hidden bg-slate-50 border border-slate-100 h-[500px] w-full relative">
            <MapView
              markers={mapMarkers}
              center={selectedVehicle?.coords || [19.076, 72.8777]}
              zoom={12}
              height="100%"
              testId="police-live-map"
            />
          </div>
        </SectionCard>
        </div>

        {/* Live Vehicles List */}
        <div className="space-y-4 bg-white rounded-2xl border border-slate-100 p-4 cus-height">
          <SectionCard>
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-slate-900">Live Vehicles</p>
                <p className="text-xs text-slate-500">{vehicles.length} vehicle{vehicles.length === 1 ? "" : "s"}</p>
              </div>
              <Badge variant="outline">{loading ? "Syncing..." : "Live"}</Badge>
            </div>
          </SectionCard>

          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {loading ? (
              <SectionCard>
                <div className="flex items-center justify-center py-8 text-slate-500">
                  <Loader2 size={16} className="mr-2 animate-spin" /> Loading vehicles...
                </div>
              </SectionCard>
            ) : vehicles.length === 0 ? (
              <SectionCard>
                <p className="text-sm text-slate-500">No vehicles available.</p>
              </SectionCard>
            ) : (
              vehicles.map((vehicle) => (
                <button
                  key={vehicle.id}
                  onClick={() => {
                    setSelectedVehicleId(vehicle.id);
                    setVehicleDialogOpen(true);
                  }}
                  className={`w-full text-left rounded-2xl border p-4 transition ${
                    selectedVehicleId === vehicle.id
                      ? "border-[hsl(var(--role-primary))] shadow-md"
                      : "border-slate-100 hover:border-slate-200"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <p className="font-semibold text-slate-900 truncate">{vehicle.registration}</p>
                    <StatusBadge status={vehicle.status} />
                  </div>
                  <p className="text-xs text-slate-500">
                    {vehicle.make && vehicle.model
                      ? `${vehicle.make} ${vehicle.model}`
                      : vehicle.make || vehicle.model || "—"}
                  </p>
                  <LocationLabel locationValue={vehicle.locationRaw ?? vehicle.location} className="mt-1 text-xs text-slate-500" />
                  <p className="mt-1 text-xs text-slate-500">
                    GPS speed {Number.isFinite(vehicle.gps_speed) ? `${vehicle.gps_speed.toFixed(1)} km/h` : "—"}
                    {Number.isFinite(vehicle.obd_speed) ? ` · OBD speed ${vehicle.obd_speed.toFixed(1)} km/h` : ""}
                  </p>
                </button>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Vehicle Detail Dialog */}
      <Dialog open={vehicleDialogOpen} onOpenChange={setVehicleDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedVehicle?.registration || "Vehicle Details"}</DialogTitle>
            <DialogDescription>{selectedVehicle?.type}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <SectionCard>
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-slate-500 uppercase">Status</p>
                  <StatusBadge status={selectedVehicle?.status} />
                </div>
              </SectionCard>
              <SectionCard>
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-slate-500 uppercase">Speed</p>
                  <p className="font-semibold text-slate-900">
                    GPS {Number.isFinite(selectedVehicle?.gps_speed) ? selectedVehicle.gps_speed.toFixed(1) : "0.0"} km/h
                    {Number.isFinite(selectedVehicle?.obd_speed) ? ` · OBD ${selectedVehicle.obd_speed.toFixed(1)} km/h` : ""}
                  </p>
                </div>
              </SectionCard>
              <SectionCard>
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-slate-500 uppercase">Make / Model</p>
                  <p className="text-sm text-slate-900">
                    {[selectedVehicle?.make, selectedVehicle?.model].filter(Boolean).join(" ") || "—"}
                  </p>
                </div>
              </SectionCard>
              <SectionCard>
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-slate-500 uppercase">Location</p>
                  <p className="text-sm text-slate-900">
                    <LocationLabel locationValue={selectedVehicle?.locationRaw ?? selectedVehicle?.location} />
                  </p>
                </div>
              </SectionCard>
              <SectionCard>
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-slate-500 uppercase">Last Updated</p>
                  <p className="text-sm text-slate-900">{formatDateTime(selectedVehicle?.lastUpdated)}</p>
                </div>
              </SectionCard>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
